import React, { useState, useEffect } from 'react';
import { quizService } from '../services/quizService';
import { useAuth } from './Auth/AuthProvider';
import logger from '../utils/logger';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import './QuizList.css';
// Hamburger menu is now rendered in the global App header

const QuizList = ({ onSelectQuiz, onCreateQuiz, onEditQuiz, onBack }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    // LocalStorageから前回選択したタブを読み込む、なければ'my'をデフォルトとする
    const savedTab = localStorage.getItem('quizListActiveTab') || 'my';
    return savedTab;
  });
  const [favoriteStates, setFavoriteStates] = useState({});
  const { user } = useAuth();

  // タブ変更とLocalStorageへの保存を処理する関数
  const handleTabChange = (tabName) => {
    setActiveTab(tabName);
    localStorage.setItem('quizListActiveTab', tabName);
    logger.userAction('change_quiz_list_tab', { 
      previousTab: activeTab, 
      newTab: tabName 
    });
  };

  // ユーザーのログイン状態に応じてタブを調整
  useEffect(() => {
    if (!user && (activeTab === 'my' || activeTab === 'favorites')) {
      // ユーザーがログアウトした場合、マイクイズやお気に入りタブからpublicに切り替え
      handleTabChange('public');
    } else if (user && activeTab === 'favorites') {
      // お気に入りタブが選択されているがuserがまだ設定されていない場合の処理は不要
      // （userが設定された後に適切にfavoritesタブが動作する）
    }
  }, [user]);

  useEffect(() => {
    logger.componentMount('QuizList');
    const onHeaderTabChange = (e) => {
      const tab = e.detail;
      if (!tab) return;
      setActiveTab(tab);
      logger.userAction('change_quiz_list_tab_from_header', { newTab: tab });
    };
    window.addEventListener('quizlist-tab-change', onHeaderTabChange);
    
    const fetchQuizzes = async () => {
      try {
        setLoading(true);
        setError(null);
        
        let fetchedQuizzes = [];
        
        if (activeTab === 'my' && user) {
          logger.userAction('fetch_user_quiz_list', { activeTab });
          fetchedQuizzes = await quizService.getUserQuizzes(user.uid);
        } else if (activeTab === 'public') {
          logger.userAction('fetch_public_quiz_list', { activeTab });
          fetchedQuizzes = await quizService.getPublicQuizzes();
        } else if (activeTab === 'favorites' && user) {
          logger.userAction('fetch_favorite_quiz_list', { activeTab });
          fetchedQuizzes = await quizService.getFavoriteQuizzes(user.uid);
        }

        // 作成者displayNameをusersコレクションから取得して付与（myタブ以外）
        if (fetchedQuizzes.length > 0 && activeTab !== 'my') {
          try {
            const uniqueUserIds = Array.from(new Set(fetchedQuizzes.map(q => q.userId).filter(Boolean)));
            const userProfiles = await Promise.all(
              uniqueUserIds.map(async (uid) => {
                try {
                  const userRef = doc(db, 'users', uid);
                  const snap = await getDoc(userRef);
                  const displayName = snap.exists() ? (snap.data()?.displayName || uid) : uid;
                  return { uid, displayName };
                } catch (e) {
                  logger.warn('Failed to fetch user displayName', { uid, error: e.message }, 'COMPONENT');
                  return { uid, displayName: uid };
                }
              })
            );
            const nameMap = userProfiles.reduce((acc, cur) => { acc[cur.uid] = cur.displayName; return acc; }, {});
            fetchedQuizzes = fetchedQuizzes.map(q => ({ ...q, authorDisplayName: nameMap[q.userId] || q.authorDisplayName || 'ユーザー' }));
          } catch (e) {
            logger.warn('DisplayName enrichment skipped due to error', { error: e.message }, 'COMPONENT');
          }
        }

        setQuizzes(fetchedQuizzes);
        
        // Load favorite states for public quizzes if user is logged in
        if (user && (activeTab === 'public' || activeTab === 'favorites')) {
          const favoriteStatesMap = {};
          for (const quiz of fetchedQuizzes) {
            if (quiz.userId !== user.uid) {
              try {
                const isFavorited = await quizService.isQuizFavorited(user.uid, quiz.userId, quiz.id);
                favoriteStatesMap[`${quiz.userId}_${quiz.id}`] = isFavorited;
              } catch (err) {
                logger.warn('Failed to check favorite state', { 
                  quizId: quiz.id,
                  quizOwnerId: quiz.userId,
                  error: err.message 
                }, 'COMPONENT');
              }
            }
          }
          setFavoriteStates(favoriteStatesMap);
        }
        
        logger.info(`QuizList loaded ${fetchedQuizzes.length} quizzes`, { 
          count: fetchedQuizzes.length,
          activeTab,
          userId: user?.uid
        }, 'COMPONENT');
      } catch (err) {
        setError('クイズの取得に失敗しました');
        logger.error('Failed to fetch quiz list', { 
          error: err.message,
          stack: err.stack,
          activeTab
        }, 'COMPONENT');
      } finally {
        setLoading(false);
      }
    };

    fetchQuizzes();
    
    return () => {
      window.removeEventListener('quizlist-tab-change', onHeaderTabChange);
      logger.componentUnmount('QuizList');
    };
  }, [user, activeTab]);

  const handleFavoriteToggle = async (quiz) => {
    if (!user) {
      logger.warn('User not logged in, cannot toggle favorite');
      return;
    }

    const isOwnQuiz = quiz.userId === user.uid;
    const favoriteKey = isOwnQuiz ? quiz.id : `${quiz.userId}_${quiz.id}`;
    const currentlyFavorited = isOwnQuiz ? quiz.isFavorite : favoriteStates[favoriteKey];

    try {
      let newFavoriteState;
      
      if (isOwnQuiz) {
        // Toggle favorite for own quiz using new isFavorite flag
        newFavoriteState = await quizService.toggleOwnQuizFavorite(user.uid, quiz.id);
        
        // Update the list: if in favorites tab and unfavorited, remove from list; otherwise update flag
        setQuizzes(prev => {
          if (activeTab === 'favorites' && !newFavoriteState) {
            return prev.filter(q => q.id !== quiz.id);
          }
          return prev.map(q => (q.id === quiz.id ? { ...q, isFavorite: newFavoriteState } : q));
        });
        
        logger.userAction(newFavoriteState ? 'add_own_quiz_favorite' : 'remove_own_quiz_favorite', { 
          quizId: quiz.id,
          quizTitle: quiz.title
        });
      } else {
        // Handle shared quiz favorites (keeping the old logic for now, but will be updated later)
        if (currentlyFavorited) {
          await quizService.removeFromFavorites(user.uid, quiz.userId, quiz.id);
          // If viewing favorites, remove from list immediately; otherwise only update state
          setFavoriteStates(prev => ({ ...prev, [favoriteKey]: false }));
          if (activeTab === 'favorites') {
            setQuizzes(prev => prev.filter(q => q.id !== quiz.id));
          }
          logger.userAction('remove_favorite', { 
            quizId: quiz.id,
            quizTitle: quiz.title,
            quizOwnerId: quiz.userId
          });
        } else {
          await quizService.addToFavorites(user.uid, quiz.userId, quiz.id);
          setFavoriteStates(prev => ({ ...prev, [favoriteKey]: true }));
          logger.userAction('add_favorite', { 
            quizId: quiz.id,
            quizTitle: quiz.title,
            quizOwnerId: quiz.userId
          });
        }
      }
    } catch (err) {
      logger.error('Failed to toggle favorite', {
        error: err.message,
        quizId: quiz.id,
        quizOwnerId: quiz.userId,
        isOwnQuiz,
        action: currentlyFavorited ? 'remove' : 'add'
      }, 'COMPONENT');
      
      // Show error message to user
      setError(`お気に入りの${currentlyFavorited ? '削除' : '追加'}に失敗しました`);
      
      // Reset error after 3 seconds
      setTimeout(() => setError(null), 3000);
    }
  };

  if (loading) {
    return <div className="quiz-list-loading">クイズを読み込み中...</div>;
  }

  const renderErrorWithTabs = () => (
    <div className="quiz-list">
      <div className="quiz-list-header">
        <div className="list-info">
            <h2>クイズ一覧</h2>
            <div className="quiz-tabs">
              <button 
                className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`}
                onClick={() => handleTabChange('my')}
                disabled={!user}
              >
                マイクイズ
              </button>
              {user && (
                <button 
                  className={`tab-btn ${activeTab === 'favorites' ? 'active' : ''}`}
                  onClick={() => handleTabChange('favorites')}
                >
                  お気に入り
                </button>
              )}
              <button 
                className={`tab-btn ${activeTab === 'public' ? 'active' : ''}`}
                onClick={() => handleTabChange('public')}
              >
                公開クイズ
              </button>
            </div>
        </div>
        <div className="header-actions">
          {onBack && (
            <button onClick={onBack} className="back-btn">
              ← 戻る
            </button>
          )}
        </div>
      </div>
      <div className="quiz-list-error">
        <p>{error}</p>
      </div>
    </div>
  );

  if (error) {
    return renderErrorWithTabs();
  }

  return (
    <div className="quiz-list">
      <div className="quiz-list-header">
        <div className="list-info">
            <h2>クイズ一覧</h2>
            <div className="quiz-tabs">
              <button 
                className={`tab-btn ${activeTab === 'my' ? 'active' : ''}`}
                onClick={() => handleTabChange('my')}
                disabled={!user}
              >
                マイクイズ
              </button>
              {user && (
                <button 
                  className={`tab-btn ${activeTab === 'favorites' ? 'active' : ''}`}
                  onClick={() => handleTabChange('favorites')}
                >
                  お気に入り
                </button>
              )}
              <button 
                className={`tab-btn ${activeTab === 'public' ? 'active' : ''}`}
                onClick={() => handleTabChange('public')}
              >
                公開クイズ
              </button>
            </div>
            <p className="list-info-details">
              全{quizzes.length}件のクイズ
            </p>
        </div>
        <div className="header-actions">
          {onBack && (
            <button onClick={onBack} className="back-btn">
              ← 戻る
            </button>
          )}
        </div>
      </div>
      
      {quizzes.length === 0 ? (
        <div className="no-quizzes">
          {activeTab === 'my' && <p>作成されたクイズがありません。</p>}
          {activeTab === 'public' && <p>公開されているクイズがありません。</p>}
          {activeTab === 'favorites' && <p>お気に入りに登録されたクイズがありません。</p>}
          {onCreateQuiz && activeTab === 'my' && (
            <button onClick={onCreateQuiz} className="create-first-quiz-btn">
              最初のクイズを作成する
            </button>
          )}
        </div>
      ) : (
        <div className="quiz-grid">
          {quizzes.map((quiz) => (
            <div key={quiz.id} className="quiz-card">
              <div className="quiz-card-header">
                <h3>{quiz.title}</h3>
                {user && (
                  <button 
                    className={`favorite-btn ${
                      quiz.userId === user.uid 
                        ? (quiz.isFavorite ? 'favorited' : '') 
                        : (favoriteStates[`${quiz.userId}_${quiz.id}`] ? 'favorited' : '')
                    }`}
                    onClick={() => handleFavoriteToggle(quiz)}
                    title={
                      quiz.userId === user.uid 
                        ? (quiz.isFavorite ? 'お気に入りから削除' : 'お気に入りに追加')
                        : (favoriteStates[`${quiz.userId}_${quiz.id}`] ? 'お気に入りから削除' : 'お気に入りに追加')
                    }
                  >
                    ★
                  </button>
                )}
              </div>
              <p className="quiz-description">{quiz.description}</p>
              <div className="quiz-metadata">
                <p className="quiz-date">
                  作成日: {new Date(quiz.createdAt).toLocaleDateString('ja-JP')}
                </p>
                {activeTab !== 'my' && (
                  <p className="quiz-author">
                    作成者: {quiz.authorDisplayName || 'ユーザー'}
                  </p>
                )}
                {activeTab === 'my' && (
                  <p className="quiz-visibility">
                    公開状態: {
                      quiz.visibility === 'private' ? '非公開' :
                      (quiz.visibility === 'public' && quiz.published === false) ? '限定公開' :
                      (quiz.visibility === 'public' && quiz.published === true) ? '公開' : '共有'
                    }
                  </p>
                )}
              </div>
              <div className="quiz-actions">
                <button 
                  className="quiz-start-btn"
                  onClick={() => {
                    logger.userAction('select_quiz', { 
                      quizId: quiz.id, 
                      quizTitle: quiz.title,
                      userId: quiz.userId,
                      activeTab
                    });
                    
                    // 公開クイズやお気に入りの場合は SharedQuiz に直接リダイレクト
                    if (activeTab === 'public' || (activeTab === 'favorites' && user && quiz.userId !== user.uid)) {
                      const sharedUrl = `/shared-quiz/${quiz.userId}/${quiz.id}`;
                      window.location.href = sharedUrl;
                    } else {
                      // マイクイズの場合は通常のフロー
                      onSelectQuiz(quiz.id, quiz.userId);
                    }
                  }}
                >
                  クイズを開始
                </button>
                {onEditQuiz && activeTab === 'my' && (
                  <button 
                    className="quiz-edit-btn"
                    onClick={() => {
                      logger.userAction('edit_quiz', { 
                        quizId: quiz.id, 
                        quizTitle: quiz.title
                      });
                      onEditQuiz(quiz.id);
                    }}
                  >
                    編集
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default QuizList;
